import React, {
	type FunctionComponent,
	type ReactNode,
	useMemo,
	useCallback,
	useEffect,
} from 'react';
import { styled } from '@compiled/react';
import { Field } from '@atlaskit/form';
import CheckIcon from '@atlaskit/icon/glyph/check';
import Lozenge from '@atlaskit/lozenge';
import { Box, xcss } from '@atlaskit/primitives';
import Select, { components, type OptionProps } from '@atlaskit/select';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { ConditionalWrapper } from '@atlassian/jira-business-conditional-wrapper';
import { shouldApplyPermissionsGate } from '@atlassian/jira-common-selectors/src/edition';
import { useIntl } from '@atlassian/jira-intl';
import ProjectAccess from '@atlassian/jira-project-access/src';
import {
	FREE_ACCESS_LEVEL,
	OPEN_ACCESS_LEVEL,
	type AccessLevel,
	getApplicationForProject,
	getEdition,
} from '@atlassian/jira-shared-types';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import CompanyManagedIcon from '../../../common/assets/CompanyManaged.svg';
import TeamManagedIcon from '../../../common/assets/TeamManaged.svg';
import { CONTACT_ADMIN_LINK, PROJECT_MANAGEMENT_TYPE_DROPDOWN_ID } from '../../../common/constants';
import { type ProjectStyle, ProjectStyles } from '../../../common/constants/project';
import type { CreateProjectConfig } from '../../../common/types';
import { Accordion } from '../../../common/ui/accordion';
import {
	useMappingState,
	useProjectData,
	useThirdPartyState,
} from '../../../controllers/import-state';
import messages from './messages';

const dropdownWidthStyles = {
	minWidth: '400px',
	maxWidth: '528px',
};

export type Props = {
	shouldForceOpen?: boolean;
	projectSettings?: CreateProjectConfig[];
	disableProjectStyle?: boolean;
	selectedEntityStyle: ProjectStyle | null;
	children?: ReactNode;
};

export type OptionType = {
	value: ProjectStyle;
	label: ReactNode;
	description: string;
	lozengeContent?: ReactNode;
	disabledContent?: ReactNode;
	disabledTooltipContent?: string;
	isDisabled: boolean;
};

const CustomOption: FunctionComponent<OptionProps<OptionType>> = (props) => {
	const {
		isDisabled,
		label,
		lozengeContent,
		description,
		disabledTooltipContent,
		disabledContent,
	} = props.data;

	return (
		<components.Option {...props}>
			<ConditionalWrapper
				condition={isDisabled}
				renderWrapper={(children) => (
					<Tooltip content={disabledTooltipContent || ''}>{children}</Tooltip>
				)}
			>
				<Box xcss={[optionWrapperStyles, ...(isDisabled ? [disabledOptionWrapperStyles] : [])]}>
					<Box>
						<Box xcss={contentWrapperStyles}>
							{label}
							{lozengeContent && (
								<Box as="span" paddingInlineStart="space.100">
									{lozengeContent}
								</Box>
							)}
						</Box>
						<Box as="p" xcss={descriptionWrapperStyles}>
							{description}
						</Box>
					</Box>
					{props.isSelected && (
						<CheckIcon primaryColor={token('color.icon.information', colors.B200)} label="" />
					)}
				</Box>
				{isDisabled && <Box xcss={disabledContentWrapperStyles}>{disabledContent}</Box>}
			</ConditionalWrapper>
		</components.Option>
	);
};

// Replace with lodash/noop
// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

export const NewProjectCreateMoreOptions = ({
	shouldForceOpen,
	projectSettings,
	disableProjectStyle,
	children,
	selectedEntityStyle,
}: Props) => {
	const { formatMessage } = useIntl();
	const [mappingState, { setMappingState }] = useMappingState();
	const [{ thirdPartyFieldConfigState, thirdPartyStatusMapping }, { setThirdPartyState }] =
		useThirdPartyState();
	const [{ type, accessLevel, style }, { setProjectData }] = useProjectData();
	const { appEditions } = useTenantContext();

	const availableProjectStyles = useMemo(
		() => projectSettings?.find((setting) => setting.productType === type)?.projectStyles,
		[projectSettings, type],
	);
	const isTMPSupported = useMemo(
		() => availableProjectStyles?.includes(ProjectStyles.TEAM_MANAGED_PROJECT) || false,
		[availableProjectStyles],
	);
	const isCMPSupported = useMemo(
		() => availableProjectStyles?.includes(ProjectStyles.COMPANY_MANAGED_PROJECT) || false,
		[availableProjectStyles],
	);
	const isPermissionGated = useMemo(() => {
		if (type && (isTMPSupported || selectedEntityStyle === ProjectStyles.TEAM_MANAGED_PROJECT)) {
			const application = getApplicationForProject(type);
			const edition = getEdition(application, appEditions);
			return shouldApplyPermissionsGate(application, edition);
		}
		return false;
	}, [appEditions, isTMPSupported, selectedEntityStyle, type]);
	const defaultProjectAccess = isPermissionGated ? FREE_ACCESS_LEVEL : OPEN_ACCESS_LEVEL;

	const companyManagedOption = useMemo(
		() => ({
			value: ProjectStyles.COMPANY_MANAGED_PROJECT,
			label: (
				<Box xcss={titleWrapperStyles}>
					<Image src={CompanyManagedIcon} aria-label="company managed" role="img" alt="" />
					<Box paddingInlineStart="space.100">{formatMessage(messages.companyManagedLabel)}</Box>
				</Box>
			),
			lozengeContent: (
				<Lozenge appearance="inprogress">{formatMessage(messages.adminOnlyLozenge)}</Lozenge>
			),
			description: formatMessage(messages.companyManagedDescription),
			disabledContent: (
				<a target="_blank" href={CONTACT_ADMIN_LINK}>
					{formatMessage(messages.contactAdminCta)}
				</a>
			),
			disabledTooltipContent: formatMessage(
				messages.cannotCreateCompanyManagedProjectTooltipMessage,
			),
			isDisabled: !isCMPSupported,
		}),
		[formatMessage, isCMPSupported],
	);

	const teamManagedOption = useMemo(
		() => ({
			value: ProjectStyles.TEAM_MANAGED_PROJECT,
			label: (
				<Box xcss={titleWrapperStyles}>
					<Image src={TeamManagedIcon} aria-label="team managed" role="img" alt="" />
					<Box paddingInlineStart="space.100">{formatMessage(messages.teamManagedLabel)}</Box>
				</Box>
			),
			description: formatMessage(messages.teamManagedDescription),
			disabledTooltipContent: formatMessage(messages.cannotCreateTeamManagedProjectTooltipMessage),
			isDisabled: !isTMPSupported,
		}),
		[formatMessage, isTMPSupported],
	);

	const options = useMemo(
		() =>
			availableProjectStyles
				? [teamManagedOption, companyManagedOption].sort(
						(a, b) =>
							availableProjectStyles.indexOf(a.value) - availableProjectStyles.indexOf(b.value),
					)
				: [teamManagedOption, companyManagedOption],
		[availableProjectStyles, companyManagedOption, teamManagedOption],
	);

	const defaultProjectStyle = useMemo(
		() => options.find(({ isDisabled }) => !isDisabled),
		[options],
	);

	const onSetProjectStyle = useCallback(
		(projectStyle: ProjectStyle) => {
			setProjectData({
				style: projectStyle,
				accessLevel:
					projectStyle === ProjectStyles.TEAM_MANAGED_PROJECT ? defaultProjectAccess : null,
			});
		},
		[defaultProjectAccess, setProjectData],
	);

	useEffect(() => {
		if (selectedEntityStyle && selectedEntityStyle !== style) {
			onSetProjectStyle(selectedEntityStyle);
			return;
		}

		if (!style && defaultProjectStyle) {
			onSetProjectStyle(defaultProjectStyle.value);
		}
	}, [defaultProjectStyle, onSetProjectStyle, selectedEntityStyle, style]);

	const selectedProjectStyle = useMemo(
		() => options.find(({ value }) => value === style),
		[options, style],
	);

	const showProjectAccessField = selectedProjectStyle?.value === ProjectStyles.TEAM_MANAGED_PROJECT;

	const onChangeProjectStyle = useCallback(
		(option: OptionType | null) => {
			if (!option?.value) {
				return;
			}

			// Reset mapping state if data has previously been loaded
			if (mappingState !== null) {
				setMappingState(null);
			}
			if (thirdPartyFieldConfigState !== null) {
				setThirdPartyState({
					thirdPartyFieldConfigState: null,
				});
			}
			if (thirdPartyStatusMapping !== null) {
				setThirdPartyState({
					thirdPartyStatusMapping: null,
				});
			}

			onSetProjectStyle(option.value);
		},
		[
			mappingState,
			onSetProjectStyle,
			setMappingState,
			setThirdPartyState,
			thirdPartyFieldConfigState,
			thirdPartyStatusMapping,
		],
	);

	const onProjectAccessChange = useCallback(
		(value: AccessLevel) => setProjectData({ accessLevel: value }),
		[setProjectData],
	);

	return (
		<Box paddingBlockStart="space.200">
			<Accordion
				shouldForceOpen={shouldForceOpen}
				heading={messages.moreOptions}
				expandedHeading={messages.hideMoreOptions}
				testId="project-create-importer-upload-flow.ui.project-create-screen.new-project-create-more-options.accordion"
			>
				<MoreOptionWrapper>
					<Field
						label={formatMessage(messages.projectType)}
						name="projectType"
						isDisabled={disableProjectStyle}
					>
						{({ fieldProps }) => (
							<Select<OptionType>
								{...fieldProps}
								inputId={PROJECT_MANAGEMENT_TYPE_DROPDOWN_ID}
								name={PROJECT_MANAGEMENT_TYPE_DROPDOWN_ID}
								options={options}
								value={selectedProjectStyle}
								onChange={onChangeProjectStyle}
								components={{
									Option: CustomOption,
								}}
							/>
						)}
					</Field>
					{showProjectAccessField && type && (
						<ProjectAccessWrapper>
							<ProjectAccess
								projectType={type}
								value={accessLevel || undefined}
								onChange={onProjectAccessChange}
								onFirstChange={noop}
								onAccessTypesLoaded={noop}
								onError={noop}
								validateRequired={false}
								requiredAccess={false}
								isDisabled={isPermissionGated}
								dropdownWidthStyles={dropdownWidthStyles}
								baseUrl=""
							/>
						</ProjectAccessWrapper>
					)}
					{children}
				</MoreOptionWrapper>
			</Accordion>
		</Box>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ProjectAccessWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'> div:first-of-type': {
		width: '100%',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MoreOptionWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	rowGap: token('space.200', '16px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	label: {
		display: 'flex',
	},
});

const optionWrapperStyles = xcss({
	display: 'flex',
	padding: 'space.100',
	paddingBottom: 'space.200',
});

const disabledOptionWrapperStyles = xcss({
	cursor: 'not-allowed',
	opacity: '0.5',
	paddingBottom: 'space.0',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Image = styled.img({
	width: token('space.300', '24px'),
	height: token('space.300', '24px'),
});

const contentWrapperStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	flexWrap: 'wrap',
});

const titleWrapperStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	font: token('font.body'),
});

const descriptionWrapperStyles = xcss({
	font: token('font.body.small'),
	marginTop: 'space.100',
	marginLeft: 'space.400',
	color: 'color.text.subtlest',
});

const disabledContentWrapperStyles = xcss({
	font: token('font.body.small'),
	paddingLeft: 'space.100',
});
